import { Injectable } from '@angular/core';
import { CustomEmoji } from '../models/bot/guild';

@Injectable({
  providedIn: 'root',
})
export class EmojiParserService {
  constructor() {}

  parseEmojiForReaction(emoji: string, customEmojis: CustomEmoji[], fieldName: string, type?: string) {
    // This function returns the DiscordEmoji instance for the reaction
    if (type === 'remove') {
      fieldName = 'Sign Off';
    } else if (type === 'late') {
      fieldName = 'Late (+5 min)';
    } else if (type === 'rlate') {
      fieldName = 'Remove Late';
    } else {
      fieldName = fieldName.replace(", ", " - ");
    }
    // The button already shows the emoji image. Drop markup that was pasted into the label.
    fieldName = this.stripCustomEmojis(fieldName).trim();

    const customEmoji = customEmojis.find((customEmoji) =>
      emoji.includes(customEmoji.id)
    );
    if (customEmoji) {
      return { url: customEmoji.imageUrl, name: customEmoji.name, count: 1, fieldName: fieldName, type: type };
    } else if (
      emoji.includes(':') &&
      emoji.includes('<') &&
      emoji.includes('>')
    ) {
      const emojiParts = emoji.match(/<a?:(\w+):(\d+)>/);
      if (emojiParts && emojiParts.length === 3) {
        const isAnimated = emoji.startsWith('<a:');
        const emojiId = emojiParts[2];
        const imageUrl = `https://cdn.discordapp.com/emojis/${emojiId}${
          isAnimated ? '.gif' : '.png'
        }`;
        return { url: imageUrl, name: emojiParts[1], count: 1, fieldName: fieldName, type: type };
      }
    }
    return { url: emoji, name: emoji, count: 1, fieldName: fieldName, type: type };
  }

  isCustomEmoji(emoji: string) {
    return emoji.includes(':') && emoji.includes('<') && emoji.includes('>')
  }

  stripCustomEmojis(text: string): string {
    if (!text) {
      return text;
    }
    return text.replace(/<a?:\w+:\d+>/g, '');
  }

  /**
   * Replace Discord custom emoji markup inside a larger string.
   * `<:name:id>` and `<a:name:id>` become image tags; surrounding text stays.
   */
  replaceCustomEmojis(text: string): string {
    if (!text) {
      return text;
    }
    return text.replace(/<a?:(\w+):(\d+)>/g, (full, name: string, id: string) =>
      this.emojiImgTag(full, name, id)
    );
  }

  /**
   * Same replacement as `replaceCustomEmojis`, with the non-emoji text escaped
   * so the result is safe to bind as HTML.
   */
  customEmojisToHtml(text: string): string {
    if (!text) {
      return '';
    }
    const pattern = /<a?:(\w+):(\d+)>/g;
    let html = '';
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      html += this.escapeHtml(text.slice(last, match.index));
      html += this.emojiImgTag(match[0], match[1], match[2]);
      last = match.index + match[0].length;
    }
    html += this.escapeHtml(text.slice(last));
    return html;
  }

  private emojiImgTag(full: string, name: string, id: string): string {
    const extension = full.startsWith('<a:') ? 'gif' : 'png';
    return `<img class="custom-button-image" src="https://cdn.discordapp.com/emojis/${id}.${extension}" alt="${name}" width="16" height="16">`;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  parseCustomEmojiForEmbed(emoji: string, customEmojis: CustomEmoji[]) {
    // This function returns an image tag with the emoji to be displayed in the embed preview
    const customEmoji = customEmojis.find(customEmoji => emoji.includes(customEmoji.id));
    if (customEmoji) {
      return `<img class="custom-button-image" src="${customEmoji.imageUrl}" alt="${customEmoji.name}"></img>`
    } else {
      // Emoji does not belong to this server. We try best effort to display the emoji anyway.
      // Emoji comes in format of `<a:name:id>` if anmiated, else `<name:id>`
      // We need to get the id and append it to the url `https://cdn.discordapp.com/emojis/<id>`
      // We also need to check if the emoji is animated. If it is, we append `.gif` to the url, else `.png`
      const emojiParts = emoji.match(/<a?:(\w+):(\d+)>/);
      if (emojiParts && emojiParts.length === 3) {
        const isAnimated = emoji.startsWith('<a:');
        const emojiId = emojiParts[2];
        const imageUrl = `https://cdn.discordapp.com/emojis/${emojiId}${isAnimated ? '.gif' : '.png'}`;
        return `<img class="custom-button-image" src="${imageUrl}" alt="${emojiParts[1]}"></img>`;
      }
    }
  }
}
